import { nomeSemAcento } from '@common/utils/nomeSemAcento';
import type { UseQueryResult } from '@tanstack/react-query';
import { useState } from 'react';
import type { UseFormGetValues, UseFormSetValue } from 'react-hook-form';
import { BRASIL, type Valores } from '../components/formulario.utils';
import type { Estado, Pais } from '../types/Formulario';
import { useBuscarCidades } from './useFormulario';

/**
 * Copia a entrega para o endereço oficial (spec 33 R8; spec 23 R7: substitui o bloco inteiro, inclusive
 * com vazio). `copiar` devolve `false` quando a cidade da entrega não casa com a lista do Axiis.
 */
export function useCopiarEntregaParaOficial({
  getValues,
  setValue,
  paises,
  estados,
}: {
  getValues: UseFormGetValues<Valores>;
  setValue: UseFormSetValue<Valores>;
  paises: UseQueryResult<Pais[]>;
  estados: UseQueryResult<Estado[]>;
}) {
  const buscarCidades = useBuscarCidades();
  const [cidadeNaoCasou, setCidadeNaoCasou] = useState(false);
  const exterior = getValues('origem') === 'exterior';
  const pronto = !!paises.data && (exterior || !!estados.data);

  const atribuir = (campo: keyof Valores['oficial'], valor: string, validar = false) =>
    setValue(`oficial.${campo}`, valor, { shouldValidate: validar });

  async function copiar(): Promise<boolean> {
    setCidadeNaoCasou(false);
    if (getValues('origem') === 'exterior') {
      const x = getValues('exterior');
      // País pelo id: a lista da entrega é a do oficial sem o Brasil (spec 23 R6).
      atribuir('paisId', x.paisId);
      atribuir('cep', x.codigoPostal);
      atribuir('cidade', x.cidade);
      atribuir('regiao', x.regiao);
      atribuir('estadoId', '');
      atribuir('cidadeId', '');
      // Acima de 300 caracteres o campo mostra o erro de tamanho para a pessoa encurtar.
      const endereco = x.endereco.replace(/\s*\n\s*/g, ', ');
      atribuir('endereco', endereco, endereco.trim().length > 300);
      // A entrega no exterior traz tudo no endereço.
      atribuir('numero', '');
      atribuir('complemento', '');
      atribuir('distrito', '');
      return true;
    }
    const en = getValues('entrega');
    atribuir('paisId', BRASIL);
    atribuir('cidade', '');
    atribuir('regiao', '');
    atribuir('cep', en.cep);
    atribuir('endereco', en.logradouro);
    atribuir('numero', en.numero);
    atribuir('complemento', en.complemento);
    atribuir('distrito', en.bairro);
    const estado = (estados.data ?? []).find((x) => x.sigla === en.uf);
    atribuir('estadoId', estado ? String(estado.id) : '');
    atribuir('cidadeId', '');
    if (!estado) {
      setCidadeNaoCasou(true);
      return false;
    }
    // O front só tem as cidades do estado que está escolhido: busca as do estado da entrega.
    // Sem a lista (Axiis fora) é o mesmo que não casar: a pessoa escolhe nos campos.
    const lista = await buscarCidades(estado.id).catch(() => []);
    const alvo = nomeSemAcento(en.cidade);
    const achadas = lista.filter((c) => nomeSemAcento(c.nome) === alvo);
    const cidade = achadas.length === 1 ? achadas[0] : null;
    atribuir('cidadeId', cidade ? String(cidade.id) : '');
    setCidadeNaoCasou(!cidade);
    return !!cidade;
  }

  return { copiar, cidadeNaoCasou, pronto };
}
